import { getDate } from 'date-fns';
import { calculateEndDate } from 'src/components/oneCorp/features/MealCredit/utils/calculateMealCreditEndDate';
import { calculateStartDate } from 'src/components/oneCorp/features/MealCredit/utils/calculateMealCreditStartDate';
import {
  getFormattedDate,
  MealCreditInfoTextProps,
} from 'src/components/oneCorp/features/MealCredit/utils/constructMealCreditInfoText.utils';
import { formatSelectedDays } from 'src/components/oneCorp/features/MealCredit/utils/formatSelectedDays';
import { formatTimeInterval } from 'src/components/oneCorp/features/MealCredit/utils/formatTimeInterval';
import { getCurrentCycleText } from 'src/components/oneCorp/features/MealCredit/utils/getCurrentCycleText';
import { getTextForRefreshUseCases } from 'src/components/oneCorp/features/MealCredit/utils/getTextForRefreshUseCases';
import { isDurationRuleValid } from 'src/components/oneCorp/features/MealCredit/utils/isDurationRuleValid';
import { getMonthAvailability } from 'src/components/oneCorp/features/MealCredit/utils/mealCreditDataUtils/getMonthAvailability';
import { getDeactivationText } from 'src/components/oneCorp/features/MealCredit/utils/getDeactivationText';
import { buildDateRangeSection } from 'src/components/oneCorp/features/MealCredit/utils/buildDateRangeSection';
import { getMonthAvailableDaysCount } from 'src/components/oneCorp/features/MealCredit/utils/getMonthAvailableDaysCount';

interface DateInfoReturnProps {
  currentCycleStartDate: Date;
  currentCycleEndDate: Date;
  formattedStartCycleDate: string;
  formattedEndCycleDate: string;
}

function getDateInfo(
  props: Partial<MealCreditInfoTextProps>
): DateInfoReturnProps {
  const {
    expirationDate,
    isRepeating,
    startDate,
    refreshPeriodDaysCount,
    refreshPeriodMonthsCount,
    isMonthAvailable,
    durationMinutes,
  } = props;

  const currentCycleStartDate = calculateStartDate({
    expirationDate,
    isRepeating,
    startDate,
    refreshPeriodDaysCount,
    refreshPeriodMonthsCount,
    isMonthAvailable,
  });

  const currentCycleEndDate = calculateEndDate({
    isMonthAvailable,
    currentCycleStartDate,
    durationMinutes,
    firstInstanceStartDate: startDate,
    refreshPeriodDaysCount,
    refreshPeriodMonthsCount,
    expirationDate,
    isRepeating,
  });

  const formattedStartCycleDate = getFormattedDate(currentCycleStartDate);
  const formattedEndCycleDate = getFormattedDate(currentCycleEndDate);

  return {
    currentCycleStartDate,
    currentCycleEndDate,
    formattedStartCycleDate,
    formattedEndCycleDate,
  };
}

export function getDurationsSummaryInfo(
  values: MealCreditInfoTextProps
): string {
  const {
    startDate,
    endTimeFullDate,
    expirationDate,
    refreshPeriodDaysCount,
    isRepeating,
    isMonthAvailable,
    budgetLength,
    budgetResetPeriod,
    budgetResetType,
    errors = {},
    expiresOn,
    monthAvailableTo,
  } = values;

  // Check if the duration rule is valid; if not, prompt the user to select settings
  if (
    !isDurationRuleValid({
      startDate,
      endTimeFullDate,
      errors,
      budgetResetPeriod,
      budgetResetType,
      budgetLength,
      expirationDate,
      expiresOn,
      isRepeating,
      refreshPeriodDaysCount,
      monthAvailableTo,
    })
  ) {
    return 'Select your settings';
  }

  const dateInfo = getDateInfo(values);
  const { isMonthAvailableEveryDay } = getMonthAvailability(
    values.isMonthAvailable,
    values.monthAvailableOn
  );

  // Determine if meal credit is available
  const isMealCreditAvailable =
    !isMonthAvailable ||
    isMonthAvailableEveryDay ||
    getDate(dateInfo.currentCycleStartDate) === getDate(startDate);

  // Build and join sections
  const sections = buildSections(values, dateInfo, isMealCreditAvailable);
  return sections.filter(Boolean).join(', ');
}

export function buildSections(
  values: MealCreditInfoTextProps,
  dateInfo: DateInfoReturnProps,
  isMealCreditAvailable: boolean
): string[] {
  const {
    startDate,
    endTimeFullDate,
    timeIntervalType,
    selectedDays,
    expirationDate,
    isRepeating,
    isDailyLengthBudget,
    isWeeklyLengthBudget,
    isMonthlyLengthBudget,
    isMonthAvailable,
    refreshPeriodMonthsCount,
    budgetResetType,
    budgetLength,
    budgetResetPeriod,
    monthAvailableTo,
    monthAvailableOn,
  } = values;

  const {
    currentCycleStartDate,
    currentCycleEndDate,
    formattedStartCycleDate,
    formattedEndCycleDate,
  } = dateInfo;

  const { isMonthAvailableOnSpecificDay, isMonthAvailableWithinDateRange } =
    getMonthAvailability(isMonthAvailable, monthAvailableOn);

  const monthAvailableDaysCount =
    isMonthAvailableWithinDateRange && monthAvailableTo
      ? getMonthAvailableDaysCount({
          startDate,
          monthAvailableTo,
          timeIntervalType,
        })
      : null;

  const sections: string[] = [];

  // Add date range section if applicable
  const dateRangeSection = buildDateRangeSection({
    isMealCreditAvailable,
    refreshPeriodMonthsCount,
    monthAvailability: {
      isMonthAvailableOnSpecificDay,
      isMonthAvailableWithinDateRange,
      monthAvailableDaysCount,
    },
    dates: {
      startDate,
      monthAvailableTo,
      currentCycleStartDate,
      currentCycleEndDate,
      expirationDate,
    },
  });

  // Add availability sections
  if (isMealCreditAvailable) {
    sections.push(dateRangeSection);
    sections.push(formatSelectedDays({ selectedDays }));
    sections.push(
      formatTimeInterval({
        startDate,
        endTimeFullDate,
        timeIntervalType,
      })
    );
  }

  // Always add cycle text
  sections.push(
    getCurrentCycleText({
      isDailyLengthBudget,
      formattedStartCycleDate,
      formattedEndCycleDate,
      isRepeating,
      isMealCreditAvailable,
      expirationDate,
    })
  );

  // Add refresh text if repeating
  if (isRepeating) {
    sections.push(
      getTextForRefreshUseCases({
        budgetLength,
        budgetResetPeriod,
        isDailyLengthBudget,
        isWeeklyLengthBudget,
        isMonthlyLengthBudget,
        budgetResetType,
        expirationDate,
      })
    );

    // Add expiration date if applicable
    if (expirationDate) {
      sections.push(getDeactivationText(expirationDate));
    }
  }

  return sections;
}
